import AutoCloseOverlay from '@/components/atoms/AutoCloseOverlay';
import Input from '@/components/V3/Input';
import { zodResolver } from '@hookform/resolvers/zod';
import { type KeyboardEventHandler, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { CPLAT_API_ROUTES, usePost } from '@/api';
import { ENTERED_BY_MARKET_NAME } from '@/constants/localstorage';
import { useMarketRegisterToken } from '@/hooks/use-market-register-token';
import { ROUTES } from '@/routes';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';

const formSchema = z.object({
  email: z.email('올바르지 않은 이메일 형식 입니다'),
  password: z.string(),
});

type FormType = z.infer<typeof formSchema>;

type SigninEmailFormProps = {
  form: SigninEmailFormController;
};

type UseSigninEmailFormOptions = {
  defaultRedirectPath?: string;
  resetPasswordPath?: string;
  onMockSignin?: () => void;
};

export const useSigninEmailForm = ({
  defaultRedirectPath = ROUTES.root,
  resetPasswordPath = ROUTES.resetPassword,
  onMockSignin,
}: UseSigninEmailFormOptions) => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const autoClose = searchParams.get('autoclose') === 'true';
  const redirectPath = searchParams.get('redirect');
  const [, setLocalStorageCplatToken] = useLocalStorage('cplatToken', '');
  const [showClosing, setShowClosing] = useState(false);
  const enteredByMarketName = localStorage.getItem(ENTERED_BY_MARKET_NAME);

  const { onConnect } = useMarketRegisterToken();

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    setError,
  } = useForm<FormType>({ resolver: zodResolver(formSchema) });

  const watchedEmail = watch('email');
  const watchedPassword = watch('password');
  const isSubmitDisabled = !watchedEmail || !watchedPassword;

  const { mutate: onSigninByEmail } = usePost(
    CPLAT_API_ROUTES.signinByEmail,
    undefined,
    undefined,
    true,
  );

  const handleLogin = useCallback(
    (values: FormType) => {
      if (onMockSignin) {
        onMockSignin();
        return;
      }

      onSigninByEmail(
        { accountEmail: values.email, plainPassword: values.password },
        {
          onSuccess: (response) => {
            const cplatToken = (response.data.data[0] as { cplatToken: string }).cplatToken;
            setLocalStorageCplatToken(cplatToken);
            localStorage.setItem('cplatToken', JSON.stringify(cplatToken));
            if (autoClose) {
              setShowClosing(true);
              setTimeout(() => {
                if (window.opener || window.history.length <= 1) {
                  window.close();
                }
                window.location.replace(ROUTES.dashboard);
              }, 1500);
              return;
            }
            if (enteredByMarketName) onConnect(cplatToken);
            else {
              localStorage.removeItem(ENTERED_BY_MARKET_NAME);
              if (redirectPath) {
                window.location.replace(redirectPath);
              } else {
                navigate(defaultRedirectPath);
              }
            }
          },
          onError: (error) => {
            const errorMessage = (error.response?.data as { message: string })?.message;
            setError('password', { message: errorMessage });
          },
        },
      );
    },
    [
      autoClose,
      defaultRedirectPath,
      enteredByMarketName,
      navigate,
      onConnect,
      onMockSignin,
      onSigninByEmail,
      redirectPath,
      setError,
      setLocalStorageCplatToken,
    ],
  );

  const submitEmail = useCallback(() => {
    void handleSubmit(handleLogin)();
  }, [handleLogin, handleSubmit]);

  const handlePasswordKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> =
    useCallback(
      (event) => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing || isSubmitDisabled) return;

        event.preventDefault();
        submitEmail();
      },
      [isSubmitDisabled, submitEmail],
    );

  return {
    action: {
      disabled: isSubmitDisabled,
      isLoading: false,
      onSubmit: submitEmail,
    },
    errors,
    handleLogin,
    handlePasswordKeyDown,
    handleSubmit,
    navigate,
    register,
    resetPasswordPath,
    showClosing,
  };
};

export type SigninEmailFormController = ReturnType<typeof useSigninEmailForm>;

export const SigninEmailForm = ({ form }: SigninEmailFormProps) => {
  const {
    action,
    errors,
    handleLogin,
    handlePasswordKeyDown,
    handleSubmit,
    navigate,
    register,
    resetPasswordPath,
    showClosing,
  } = form;

  return (
    <>
      {showClosing && <AutoCloseOverlay />}
      <form onSubmit={handleSubmit(handleLogin)}>
        <div className="space-y-3">
          <div className="flex flex-col items-start gap-2">
            <span className="text-gr-800 Caption1M12">이메일</span>
            <Input
              placeholder="이메일 주소를 입력해 주세요"
              {...register('email')}
              Error={{ isError: !!errors.email?.message, errorMessage: errors.email?.message }}
            />
          </div>
          <div className="flex flex-col items-start gap-2">
            <span className="text-gr-800 Caption1M12">비밀번호</span>
            <Input
              placeholder="비밀번호를 입력해 주세요"
              {...register('password')}
              onKeyDown={handlePasswordKeyDown}
              type="password"
              Error={{
                isError: !!errors.password?.message,
                errorMessage: errors.password?.message,
              }}
            />
          </div>
          <div className="flex justify-end">
            <button
              className="text-gr-700 underline underline-offset-2 Body5M14"
              type="button"
              onClick={() => navigate(resetPasswordPath)}
            >
              비밀번호 재설정
            </button>
          </div>
          <button
            aria-hidden="true"
            className="sr-only"
            disabled={action.disabled}
            tabIndex={-1}
            type="submit"
          >
            로그인
          </button>
        </div>
      </form>
    </>
  );
};
